import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Send, Briefcase, AlertCircle, X, Shield, ArrowLeft, ArrowRight, Calendar, MapPin, LogOut, Settings, Reply, Users, Download, RotateCcw, ChevronDown, ArrowBigUp, ArrowBigDown, Flag, Flame, TrendingUp, Clock } from "lucide-react";
import api, { apiErrorMessage, productUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import IntentDateModal from "../components/IntentDateModal";
import RoomDealsBox from "../components/RoomDealsBox";
import BhegaButton from "../components/BhegaButton";
import useLiveCount from "../lib/useLiveCount";
import { useActivity } from "../lib/activity";
import useEscape from "../lib/useEscape";

const ALIAS_PALETTE = [
  "#155E75", "#9F1239", "#166534", "#7A4A00", "#9A3412",
  "#4B5563", "#1E40AF", "#B42318", "#0F766E", "#57534E",
];
function colorForAlias(alias) {
  if (!alias) return ALIAS_PALETTE[0];
  let h = 0;
  for (let i = 0; i < alias.length; i++) h = (h * 31 + alias.charCodeAt(i)) >>> 0;
  return ALIAS_PALETTE[h % ALIAS_PALETTE.length];
}
function dayLabel(iso) {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function shortDate(iso) {
  if (!iso) return "";
  const s = String(iso);
  if (s === "ASAP") return "As soon as possible";
  if (/^\d{4}-\d{2}$/.test(s)) {
    return new Date(`${s}-01`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  }
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function shortMonthCompact(iso) {
  if (!iso) return "";
  const s = String(iso);
  if (s === "ASAP") return "ASAP";
  if (/^\d{4}-\d{2}$/.test(s)) {
    return new Date(`${s}-01`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  }
  return s;
}
const EXIT_REASONS = [
  { key: "group_deal", label: "We made a group deal" },
  { key: "better_deal", label: "I found a better deal elsewhere" },
  { key: "no_longer", label: "I no longer intend to purchase" },
];

export default function NegotiationRoomPage({ onOpenAuth, productOverride }) {
  const params = useParams();
  const { user, ready, city } = useAuth();
  const navigate = useNavigate();
  const activity = useActivity();
  const unmarkJoined = activity?.unmarkJoined;
  const [unreadSnap, setUnreadSnap] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const composerRef = useRef(null);
  const seenRefreshedRef = useRef(false);
  const isAdmin = user?.role === "admin";
  const [product, setProduct] = useState(productOverride || null);
  const liveCount = useLiveCount(product);
  const productId = product?.id || params.productId || params.id;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hireStatus, setHireStatus] = useState(null);
  const [showHire, setShowHire] = useState(false);
  const [intentDate, setIntentDate] = useState(null);
  const isPending = intentDate === "pending";  // preview-only state: user joined via "I will decide later"
  const [intentLoaded, setIntentLoaded] = useState(false);
  const [showIntent, setShowIntent] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showAdminBuyers, setShowAdminBuyers] = useState(false);
  const [filterCity, setFilterCity] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [availableCities, setAvailableCities] = useState([]);
  // Default the city filter to the user's current city as soon as we know it.
  // Per spec: there is NO 'All cities' option — the room filters by city by default.
  useEffect(() => {
    if (city && filterCity === "all") setFilterCity(city);
    // eslint-disable-next-line
  }, [city]);
  const [replyTo, setReplyTo] = useState(null);
  const [sortMode, setSortMode] = useState("new"); // 'best' | 'top' | 'new' | 'controversial'
  const [voting, setVoting] = useState({}); // { [msgId]: bool }
  const listRef = useRef(null);
  const followBottomRef = useRef(true); // true → auto-scroll new messages into view
  const lastMsgIdRef = useRef(null);
  const settingsRef = useRef(null);
  const cityPopoverRef = useRef(null);
  const [showCityPopover, setShowCityPopover] = useState(false);
  const [nearbyCities, setNearbyCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [citySearchResults, setCitySearchResults] = useState([]);

  // SEO meta tags — set whenever product changes
  useEffect(() => {
    if (!product) return;
    const title = `${product.product || product.name} — Negotiation Room | BHEGA`;
    const desc = `Unite ${product.bhega_count || 0} buyers and negotiate the best deal on ${product.product || product.name} (${product.brand || ""}).`;
    document.title = title;
    const set = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    set("description", desc);
    set("og:title", title, "property");
    set("og:description", desc, "property");
    return () => { document.title = "BHEGA"; };
  }, [product]);

  // Close popovers on outside click
  useEffect(() => {
    if (!showSettings && !showCityPopover) return;
    const onDoc = (e) => {
      if (showSettings && settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false);
      if (showCityPopover && cityPopoverRef.current && !cityPopoverRef.current.contains(e.target)) setShowCityPopover(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showSettings, showCityPopover]);

  // Load nearby cities (50km) the first time the city popover opens, then cache.
  useEffect(() => {
    if (!showCityPopover) return;
    if (!city) return;
    if (nearbyCities.length) return;
    api.get("/geo/nearby-cities", { params: { city, radius_km: 50, limit: 20 } })
      .then((r) => setNearbyCities(r.data.cities || []))
      .catch(() => setNearbyCities([]));
  }, [showCityPopover, city, nearbyCities.length]);

  // Debounced free-text city search via the existing /cities endpoint.
  useEffect(() => {
    if (!showCityPopover) return;
    const q = citySearch.trim();
    if (q.length < 2) { setCitySearchResults([]); return; }
    let active = true;
    const id = setTimeout(() => {
      api.get("/cities", { params: { q, limit: 15 } })
        .then((r) => { if (active) setCitySearchResults(r.data.cities || []); })
        .catch(() => { if (active) setCitySearchResults([]); });
    }, 250);
    return () => { active = false; clearTimeout(id); };
  }, [citySearch, showCityPopover]);

  const fetchMessages = async () => {
    if (!productId) return;
    // Preview / pending users see ALL chats — no city/date scoping.
    const effCity = isPending ? "all" : filterCity;
    const effDate = isPending ? "all" : filterDate;
    const qs = { filter_city: effCity, filter_date: effDate, sort: sortMode };
    if (city) qs.city = city;
    const m = await api.get(`/rooms/${productId}/messages`, { params: qs });
    setMessages(m.data.messages || []);
    setAvailableCities(m.data.available_cities || []);
    if (user) {
      api.post(`/rooms/${productId}/seen`, null, { params: city ? { city } : {} })
        .then(() => { if (!seenRefreshedRef.current) { seenRefreshedRef.current = true; activity?.refresh(); } })
        .catch(() => {});
    }
  };

  // Fetch product + hire-status as soon as the page mounts
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!productOverride && productId) {
          const p = await api.get(`/products/${productId}`, { params: city ? { city } : {} });
          if (!active) return;
          setProduct(p.data);
        }
      } catch { /* product may be gone */ }
      if (!user || !productId) return;
      try {
        const h = await api.get(`/products/${productId}/hire-status`);
        if (active) setHireStatus(h.data);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [user, productId, city, productOverride]);

  // Load intent date once user is known — admin skips this gate entirely.
  useEffect(() => {
    if (!user) return;
    if (isAdmin) { setIntentLoaded(true); setIntentDate("admin"); return; }
    let active = true;
    api.get(`/products/${productId}/intent-calendar`, { params: city ? { city } : {} })
      .then((r) => { if (!active) return; setIntentDate(r.data.my_date || null); })
      .finally(() => { if (active) setIntentLoaded(true); });
    return () => { active = false; };
  }, [user, productId, city, isAdmin]);

  // Fetch + poll messages once gate cleared
  useEffect(() => {
    if (!user || !intentDate) return;
    fetchMessages();
    const id = setInterval(fetchMessages, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [productId, user, city, intentDate, filterCity, filterDate, sortMode]);

  // Sticky scroll: only auto-scroll to bottom if user is already near the bottom
  // (i.e. actively following the conversation). If they've scrolled up to read
  // history, leave their viewport alone — even when polling brings in new
  // messages.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const last = messages.length ? messages[messages.length - 1] : null;
    const newLastId = last ? last.id : null;
    if (newLastId === lastMsgIdRef.current) return; // no change
    lastMsgIdRef.current = newLastId;
    if (followBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onMessagesScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    followBottomRef.current = nearBottom;
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 300);
  };

  const jumpToLatest = () => {
    const el = listRef.current;
    if (!el) return;
    followBottomRef.current = true;
    el.scrollTop = el.scrollHeight;
    setShowJump(false);
  };

  const castVote = async (msg, direction) => {
    if (!user || isPending || isAdmin) return;
    const next = (msg.my_vote === direction) ? "clear" : direction;
    setVoting((v) => ({ ...v, [msg.id]: true }));
    // Optimistic update
    setMessages((prev) => prev.map((it) => {
      if (it.id !== msg.id) return it;
      const wasUp = it.my_vote === "up";
      const wasDown = it.my_vote === "down";
      let up = it.upvote_count || 0;
      let down = it.downvote_count || 0;
      if (wasUp) up--;
      if (wasDown) down--;
      if (next === "up") up++;
      else if (next === "down") down++;
      return { ...it, upvote_count: up, downvote_count: down, score: up - down, my_vote: next === "clear" ? null : next };
    }));
    try {
      await api.post(`/rooms/${productId}/messages/${msg.id}/vote`, { direction: next });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Vote failed"));
      fetchMessages();
    } finally {
      setVoting((v) => { const c = { ...v }; delete c[msg.id]; return c; });
    }
  };

  const reportMsg = async (msg) => {
    if (!user || isPending || isAdmin) return;
    if (msg.my_report) { toast.info("Already reported."); return; }
    if (!window.confirm("Report this message? 5 reports auto-hide the message and send it for admin review.")) return;
    try {
      const r = await api.post(`/rooms/${productId}/messages/${msg.id}/report`);
      toast.success(r.data.escalated ? "Reported. Sent to admin review." : "Reported.");
      setMessages((prev) => prev.map((it) => it.id === msg.id ? { ...it, my_report: true, report_count: r.data.report_count, is_pending_review: r.data.escalated } : it));
      if (r.data.escalated) fetchMessages();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not report")); }
  };

  // Live count refresh on bhega:changed
  useEffect(() => {
    const onChange = async (ev) => {
      if (!productId) return;
      if (ev?.detail?.productId && ev.detail.productId !== productId) return;
      try {
        const r = await api.get(`/products/${productId}`, { params: city ? { city } : {} });
        setProduct((cur) => (cur ? { ...cur, bhega_count: r.data.bhega_count } : cur));
      } catch (_) { /* noop */ }
    };
    window.addEventListener("bhega:changed", onChange);
    return () => window.removeEventListener("bhega:changed", onChange);
  }, [productId, city]);

  // Snapshot unread count on entry so we can show a "new since last visit" divider.
  // NOTE: reset effect must be declared BEFORE the snapshot effect — otherwise on mount the
  // reset (null) overwrites the snapshot and React bails out (null === initial null).
  useEffect(() => { setUnreadSnap(null); seenRefreshedRef.current = false; }, [productId]);
  useEffect(() => {
    if (unreadSnap !== null || !activity?.loaded || !productId) return;
    const r = (activity.rooms || []).find((x) => x.product_id === productId);
    setUnreadSnap(r ? (r.unread_count || 0) : 0);
  }, [activity?.loaded, activity?.rooms, productId, unreadSnap]);

  if (!ready) return <div className="container-x py-16"><div className="skeleton h-8 w-64" /><div className="skeleton h-[50vh] w-full mt-6" /></div>;

  if (!user) {
    return (
      <div className="container-x py-12 sm:py-16">
        <div className="max-w-2xl mx-auto bhega-card border border-border">
          <div className="h-1 bg-[#FFBE06]" aria-hidden />
          <div className="p-6 sm:p-10">
            <div className="eyebrow">Negotiation room</div>
            <h1 className="t-page mt-2">
              {product ? (product.product || product.name) : "Sign in to enter the room"}
            </h1>
            {product && (
              <p className="mt-2 text-muted-foreground">
                <strong className="text-foreground num">✊ {product.bhega_count || 0}</strong> buyers are planning to buy this. Sign in to join them.
              </p>
            )}
            <ul className="mt-6 space-y-2 text-sm text-[var(--bhega-text)]">
              <li className="flex gap-2"><Shield size={16} className="text-brand shrink-0 mt-0.5" /> Everyone speaks as an anonymous <strong>Buyer #XXXX</strong> — your name and phone stay private.</li>
              <li className="flex gap-2"><Calendar size={16} className="text-brand shrink-0 mt-0.5" /> You&apos;ll be grouped with buyers purchasing in the same month.</li>
              <li className="flex gap-2"><Users size={16} className="text-brand shrink-0 mt-0.5" /> Share quotes and negotiate with sellers as a group.</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onOpenAuth} className="btn-primary" data-testid="room-signin-btn">Sign in to continue</button>
              <Link to={product ? `/browse?q=${encodeURIComponent(product.product || product.name || "")}` : "/browse"} className="btn-outline" data-testid="room-back-to-product">Back to products</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product || !intentLoaded) return <div className="container-x py-16"><div className="skeleton h-8 w-64" /><div className="skeleton h-[50vh] w-full mt-6" /></div>;

  // Intended-month gate — mandatory on first visit. Admin skips.
  if (!intentDate) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <button onClick={() => navigate(-1)} className="btn-ghost text-xs -ml-3 mb-3" data-testid="room-back">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="bhega-card p-5 sm:p-6 mb-4 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="eyebrow">You&apos;re raising your fist for</div>
            <h1 className="t-page mt-1">{product.product || product.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Pick <strong className="text-foreground">when you plan to buy</strong>. We&apos;ll put you in a room with buyers on the same timeline. You can change it later.
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-extrabold num">✊ {product.bhega_count || 0}</div>
            <div className="eyebrow">buyers united</div>
          </div>
        </div>
        <IntentDateModal
          productId={productId}
          city={city}
          productName={product.product || product.name}
          onClose={() => {
            // Let keyboard users out of the mandatory gate: back, or home on a direct deep link.
            if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
            else navigate("/");
          }}
          onSaved={(d) => setIntentDate(d)}
          onDecideLater={() => setIntentDate("pending")}
        />
      </div>
    );
  }



  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const params = {};
      if (city) params.city = city;
      if (intentDate && intentDate !== "admin") params.date = intentDate;
      const body = { text: text.trim() };
      if (replyTo?.id) body.reply_to_id = replyTo.id;
      await api.post(`/rooms/${productId}/messages`, body, { params });
      setText(""); setReplyTo(null);
      if (composerRef.current) composerRef.current.style.height = "auto";
      followBottomRef.current = true;
      await fetchMessages();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send"));
    } finally { setBusy(false); }
  };

  const stateLabel = (() => {
    if (!product?.scope) return null;
    if (product.scope === "global") return "Open to buyers across India";
    const st = product.scope.replace(/^state:/, "");
    return st && st !== "Unknown" ? `${st} buyers room` : null;
  })();

  const filterDateLabel = filterDate === "all"
    ? "All months"
    : shortDate(filterDate);

  const canInteract = !isAdmin && !isPending;
  const count = product.bhega_count || 0;
  const MILESTONE = 10;
  const toMilestone = Math.max(0, MILESTONE - count);
  const newDividerId = (() => {
    if (!unreadSnap || sortMode !== "new") return null;
    let c = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (!messages[i].is_me) { c++; if (c === unreadSnap) return messages[i].id; }
    }
    return null;
  })();

  return (
    <div className="max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
      <div className={`grid grid-cols-1 gap-5 ${isAdmin ? "xl:grid-cols-[minmax(0,1fr)_330px]" : "lg:grid-cols-[230px_minmax(0,1fr)] xl:grid-cols-[230px_minmax(0,1fr)_330px]"}`}>
        {!isAdmin && <RoomsRail activeId={productId} />}

        <section className="min-w-0 flex flex-col" aria-label="Conversation">
          {/* Room identity */}
          <div className="bhega-card p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => navigate(-1)} className="btn-ghost text-xs -ml-2 min-h-[32px]" data-testid="room-back">
                <ArrowLeft size={14} /> Back
              </button>
              <div className="eyebrow truncate">
                Negotiation room{stateLabel && <span className="normal-case tracking-normal font-semibold"> · {stateLabel}</span>}
                {isAdmin && <span className="ml-2 bhega-pill pill-dark">Admin view</span>}
              </div>
            </div>
            <div className="mt-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="t-page" data-testid="room-product-title">
                  {product.product || product.name}
                </h1>
                <div className="text-[13.5px] text-muted-foreground mt-1">
                  <strong className="text-foreground">{product.brand}</strong>{(product.typology || product.variant) ? ` · ${product.typology || product.variant}` : ""} · {product.category_label}
                </div>
              </div>
              <div className="xl:hidden text-right shrink-0">
                <div className="text-2xl font-extrabold num leading-none">✊ {count}</div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mt-1">united</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {!isAdmin && intentDate !== "admin" && !isPending && (
                <>
                  <span className="bhega-pill pill-yellow" data-testid="room-intended-on">
                    <Calendar size={12} /> Buying in {shortDate(intentDate)}
                  </span>
                  <button onClick={() => setShowIntent(true)} className="btn-outline text-xs" data-testid="room-view-calendar">
                    Change month
                  </button>
                </>
              )}
              {isPending && (
                <>
                  <span className="bhega-pill pill-dark" data-testid="room-preview-badge">Preview mode · read only</span>
                  <BhegaButton product={product} count={liveCount} size="md" full={false} onClick={() => setShowIntent(true)} testId="room-join-now-btn" />
                </>
              )}
              {!isAdmin && (
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => setShowSettings((v) => !v)}
                    className="btn-outline text-xs"
                    data-testid="room-settings-btn"
                    aria-haspopup="menu"
                    aria-expanded={showSettings}
                  >
                    <Settings size={13} /> Room settings
                  </button>
                  {showSettings && (
                    <div className="absolute z-30 mt-1 left-0 sm:left-auto sm:right-0 bg-card border border-border min-w-[250px] py-1" role="menu" data-testid="room-settings-menu">
                      <button
                        onClick={() => { setShowSettings(false); setShowExit(true); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-[var(--bhega-danger-soft)] text-[var(--bhega-danger)] font-bold flex items-center gap-2"
                        data-testid="room-settings-exit"
                        role="menuitem"
                      >
                        <LogOut size={14} /> Exit the negotiation room
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isAdmin && (
                <button onClick={() => setShowAdminBuyers(true)} className="btn-primary text-xs" data-testid="room-admin-buyers-btn">
                  <Users size={13} /> Intended buyers
                </button>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              <Shield size={12} className="inline -mt-0.5 mr-1" /> Names &amp; phone numbers are hidden. Everyone speaks as <strong className="text-foreground">Buyer #XXXX</strong>.
            </p>
          </div>

          {/* Conversation */}
          <div className="mt-4 bhega-card flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-border px-2 sm:px-3 py-2">
              <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" role="tablist" aria-label="Sort messages" data-testid="room-sort-tabs">
                {[
                  { k: "new", label: "Latest", icon: <Clock size={12} /> },
                  { k: "best", label: "Best", icon: <Flame size={12} /> },
                  { k: "top", label: "Top", icon: <TrendingUp size={12} /> },
                  { k: "controversial", label: "Debated", icon: <AlertCircle size={12} /> },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    role="tab"
                    aria-selected={sortMode === t.k}
                    onClick={() => setSortMode(t.k)}
                    className={`chip shrink-0 ${sortMode === t.k ? "chip-active" : "border-transparent"}`}
                    data-testid={`room-sort-${t.k}`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-muted-foreground shrink-0 num hidden sm:inline">{messages.length} msgs</span>
            </div>

            <div className="relative">
              <div
                ref={listRef}
                onScroll={onMessagesScroll}
                className="chat-surface h-[58vh] lg:h-[max(520px,calc(100vh-340px))] min-h-[340px] overflow-y-auto py-2"
                data-testid="room-messages"
                aria-live="polite"
              >
                {messages.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="room-empty">
                    <div className="text-4xl" aria-hidden>💬</div>
                    <p className="mt-3 font-extrabold">No messages here yet</p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                      {canInteract ? "Break the ice — share a quote you received, a dealer you visited, or a question for fellow buyers." : "No messages match the current filters. Try widening them."}
                    </p>
                  </div>
                )}
                {messages.map((m, idx) => {
                  const prev = idx > 0 ? messages[idx - 1] : null;
                  const showDate = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
                  const showNew = newDividerId === m.id;
                  const grouped =
                    !!prev && prev.alias === m.alias && !!prev.is_me === !!m.is_me &&
                    (prev.sender_city || "") === (m.sender_city || "") &&
                    !showDate && !showNew && !m.reply_to &&
                    (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60000;
                  const aliasColor = colorForAlias(m.alias);
                  const initials = (m.alias || "").replace(/[^0-9a-z]/gi, "").slice(-2).toUpperCase() || "B";
                  const senderCityShort = (m.sender_city || "").split(",")[0].trim();
                  const time = new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                  const score = m.score ?? ((m.upvote_count || 0) - (m.downvote_count || 0));
                  const voteBusy = !!voting[m.id];
                  const isGuide = !!m.is_guide || m.sender_type === "guide";
                  return (
                    <React.Fragment key={m.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 px-4 my-3" data-testid={`room-msg-date-${m.id}`}>
                          <span className="flex-1 h-px bg-[var(--bhega-border)]" />
                          <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{dayLabel(m.created_at)}</span>
                          <span className="flex-1 h-px bg-[var(--bhega-border)]" />
                        </div>
                      )}
                      {showNew && (
                        <div className="flex items-center gap-3 px-4 my-2" data-testid="room-new-divider">
                          <span className="flex-1 h-[2px] bg-signal" />
                          <span className="text-xs font-extrabold uppercase tracking-[0.1em] text-signal">New since your last visit</span>
                          <span className="flex-1 h-[2px] bg-signal" />
                        </div>
                      )}
                      <article
                        className={`group flex gap-3 px-3 sm:px-4 ${grouped ? "pt-0.5 pb-1.5" : "pt-2.5 pb-1.5"} transition-colors ${
                          m.is_me ? "bg-[var(--bhega-primary-surface)] border-l-[3px] border-[#FFBE06]" : "border-l-[3px] border-transparent hover:bg-raised"
                        } ${m.is_pending_review ? "opacity-70" : ""}`}
                        data-testid={`room-msg-${m.id}`}
                      >
                        {grouped
                          ? <span className="w-[34px] shrink-0" aria-hidden />
                          : isGuide
                            ? <span className="avatar-sq" style={{ background: "#111111", color: "#FFBE06" }} aria-hidden>BG</span>
                            : <span className="avatar-sq" style={{ background: aliasColor }} aria-hidden>{initials}</span>}
                        <div className="flex-1 min-w-0">
                          {!grouped && (
                            <header className="flex items-baseline gap-1.5 flex-wrap text-[12px] text-muted-foreground">
                              {isGuide
                                ? <span className="px-1.5 bg-[#111111] text-[#FFBE06] text-xs uppercase tracking-wider font-extrabold" data-testid={`room-msg-guide-badge-${m.id}`}>Bhega Guide · automated</span>
                                : <strong className="font-extrabold text-[13px] text-foreground">{m.alias}</strong>}
                              {m.is_me && <span className="px-1 bg-[#FFBE06] text-[#111111] text-xs uppercase tracking-wider font-extrabold">you</span>}
                              {senderCityShort && <span>· {senderCityShort}</span>}
                              {m.sender_date && <span>· {shortMonthCompact(m.sender_date)}</span>}
                              <span title={new Date(m.created_at).toLocaleString("en-IN")}>· {time}</span>
                              {m.is_pending_review && <span className="px-1.5 bg-[var(--bhega-danger-soft)] text-[var(--bhega-danger)] text-xs uppercase tracking-wider font-extrabold">pending review</span>}
                            </header>
                          )}
                          {m.reply_to && (
                            <div className="mt-1 border-l-[3px] border-[#FFBE06] bg-card px-2.5 py-1.5 text-[12px] max-w-[640px]">
                              <div className="font-bold text-foreground">{m.reply_to.alias}</div>
                              <div className="text-muted-foreground line-clamp-2">{m.reply_to.text_preview}</div>
                            </div>
                          )}
                          <p className="mt-0.5 t-chat text-foreground whitespace-pre-wrap break-words max-w-[680px]">{m.text}</p>
                          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            {!isGuide && <div className="inline-flex items-center border border-border bg-card">
                              <button
                                type="button"
                                onClick={() => castVote(m, "up")}
                                disabled={!canInteract || voteBusy}
                                className={`h-7 w-7 inline-flex items-center justify-center transition-colors ${m.my_vote === "up" ? "text-[var(--bhega-success)]" : "hover:text-[var(--bhega-success)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                                data-testid={`room-msg-upvote-${m.id}`}
                                aria-label="Helpful (upvote)"
                                aria-pressed={m.my_vote === "up"}
                              >
                                <ArrowBigUp size={17} fill={m.my_vote === "up" ? "currentColor" : "none"} />
                              </button>
                              <span className={`min-w-[22px] text-center font-extrabold num ${m.my_vote === "up" ? "text-[var(--bhega-success)]" : m.my_vote === "down" ? "text-[var(--bhega-danger)]" : "text-foreground"}`} data-testid={`room-msg-score-${m.id}`}>
                                {score}
                              </span>
                              <button
                                type="button"
                                onClick={() => castVote(m, "down")}
                                disabled={!canInteract || voteBusy}
                                className={`h-7 w-7 inline-flex items-center justify-center transition-colors ${m.my_vote === "down" ? "text-[var(--bhega-danger)]" : "hover:text-[var(--bhega-danger)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                                data-testid={`room-msg-downvote-${m.id}`}
                                aria-label="Not helpful (downvote)"
                                aria-pressed={m.my_vote === "down"}
                              >
                                <ArrowBigDown size={17} fill={m.my_vote === "down" ? "currentColor" : "none"} />
                              </button>
                            </div>}
                            {canInteract && (
                              <button
                                type="button"
                                onClick={() => { setReplyTo({ id: m.id, alias: m.alias, text_preview: (m.text || "").slice(0, 140), sender_city: m.sender_city, sender_date: m.sender_date }); composerRef.current?.focus(); }}
                                className="h-7 px-2 inline-flex items-center gap-1 hover:text-foreground hover:bg-card font-bold"
                                data-testid={`room-msg-reply-${m.id}`}
                              >
                                <Reply size={13} /> Reply
                              </button>
                            )}
                            {canInteract && !m.is_me && !isGuide && (
                              <button
                                type="button"
                                onClick={() => reportMsg(m)}
                                disabled={m.my_report}
                                className={`h-7 px-2 inline-flex items-center gap-1 font-bold ${m.my_report ? "text-[var(--bhega-danger)]" : "hover:text-[var(--bhega-danger)]"} disabled:opacity-60`}
                                data-testid={`room-msg-report-${m.id}`}
                              >
                                <Flag size={12} /> {m.my_report ? "Reported" : "Report"}
                                {m.report_count > 0 && <span className="text-xs num">({m.report_count})</span>}
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    </React.Fragment>
                  );
                })}
              </div>
              {showJump && (
                <button onClick={jumpToLatest} className="btn-dark text-xs absolute bottom-3 left-1/2 -translate-x-1/2" data-testid="room-jump-latest">
                  <ChevronDown size={14} /> Jump to latest
                </button>
              )}
            </div>

            {canInteract && replyTo && (
              <div className="border-t border-border px-3 py-2 bg-[var(--bhega-primary-surface)] flex items-start gap-2" data-testid="room-reply-preview">
                <div className="flex-1 border-l-[3px] border-[#FFBE06] pl-2 min-w-0">
                  <div className="text-xs font-extrabold text-foreground">Replying to {replyTo.alias}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{replyTo.text_preview}</div>
                </div>
                <button onClick={() => setReplyTo(null)} className="icon-btn w-8 h-8" data-testid="room-reply-cancel" aria-label="Cancel reply"><X size={14} /></button>
              </div>
            )}

            {canInteract ? (
              <form
                className="sticky md:static bottom-[calc(60px+env(safe-area-inset-bottom))] z-10 border-t border-border p-2 sm:p-3 flex gap-2 items-end bg-card"
                onSubmit={(e) => { e.preventDefault(); send(); }}
              >
                <label htmlFor="room-composer" className="sr-only">Write a message</label>
                <textarea
                  id="room-composer"
                  ref={composerRef}
                  rows={1}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={replyTo ? `Reply to ${replyTo.alias}…` : "Share a quote or ask fellow buyers…"}
                  className="input-bhega flex-1 resize-none leading-snug py-2.5"
                  data-testid="room-input"
                  maxLength={2000}
                />
                <button
                  type="submit"
                  disabled={busy || !text.trim()}
                  aria-label="Send message"
                  className="btn-primary w-11 px-0 shrink-0"
                  data-testid="room-send"
                >
                  <Send size={17} />
                </button>
              </form>
            ) : isPending ? (
              <div className="border-t border-border px-4 py-3 bg-[var(--bhega-primary-surface)] flex flex-wrap items-center justify-between gap-3" data-testid="room-pending-banner">
                <div className="text-sm">
                  <strong>You&apos;re previewing this room.</strong> Pick your buying month to join the conversation.
                </div>
                <BhegaButton product={product} count={liveCount} size="lg" onClick={() => setShowIntent(true)} testId="room-pending-join-btn" />
              </div>
            ) : (
              <div className="border-t border-border px-4 py-3 bg-raised text-xs text-muted-foreground text-center">
                Admin view · chat is read-only.
              </div>
            )}
          </div>
        </section>

        <div className={`space-y-4 self-start ${isAdmin ? "" : "lg:col-start-2"} xl:col-start-auto xl:sticky xl:top-[100px]`} data-testid="room-right-rail">
          {/* Collective buying power */}
          <div className="bhega-card border border-border" data-testid="room-power-panel">
            <div className="p-4">
              <div className="eyebrow">Collective buying power</div>
              <div className="mt-2 flex items-end justify-between gap-3">
                <div>
                  <div className="text-5xl font-extrabold num leading-none" data-testid="room-bhega-badge">{count}</div>
                  <div className="text-sm text-muted-foreground mt-1">buyer{count === 1 ? "" : "s"} united ✊</div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span>Negotiator milestone</span>
                  <span className="num">{Math.min(count, MILESTONE)}/{MILESTONE}</span>
                </div>
                <div className="progress-track mt-1.5"><div className="progress-fill" style={{ width: `${Math.min(100, (count / MILESTONE) * 100)}%` }} /></div>
                <p className="text-[12px] text-muted-foreground mt-1.5" data-testid="room-milestone-text">
                  {toMilestone > 0
                    ? <>{toMilestone} more buyer{toMilestone === 1 ? "" : "s"} and this group can call in a professional negotiator. Share the room to get there faster.</>
                    : <>This group is big enough to bring in a professional negotiator.</>}
                </p>
              </div>
            </div>
            {!isAdmin && (
              <div className="border-t border-border p-4 bg-raised">
                <div className="font-bold text-sm flex items-center gap-2"><Briefcase size={14} /> Hire a professional negotiator</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {hireStatus?.has_request
                    ? `A request is already raised by ${hireStatus.by_me ? "you" : "another buyer"} (${hireStatus.intended_buyers} intended buyers).`
                    : "Available once at least 10 intended buyers are ready. One request per product."}
                </div>
                <button
                  disabled={hireStatus?.has_request}
                  onClick={() => setShowHire(true)}
                  className="btn-dark text-xs w-full mt-3"
                  data-testid="hire-negotiator-btn"
                >
                  {hireStatus?.has_request ? "Already requested" : "Hire Negotiator"}
                </button>
              </div>
            )}
          </div>


        <aside className="bhega-card p-4" data-testid="room-filters">
          <div className="font-extrabold text-sm">Filter the conversation</div>
          <p className="text-xs text-muted-foreground mt-1">
            See what buyers from a specific city or with a specific intended date are negotiating.
          </p>

          <div className="mt-4 relative" ref={cityPopoverRef}>
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1">
              <MapPin size={11} /> City
            </label>
            <button
              type="button"
              onClick={() => setShowCityPopover((v) => !v)}
              className="mt-1 w-full border border-border rounded-none px-2 py-2 text-sm bg-card text-left hover:border-foreground/30 flex items-center justify-between gap-2"
              data-testid="room-filter-city"
              aria-haspopup="dialog"
              aria-expanded={showCityPopover}
            >
              <span className="truncate">{filterCity === "all" ? (city || "Pick a city") : filterCity}</span>
              <ChevronDown size={14} className="text-muted-foreground shrink-0" />
            </button>

            {showCityPopover && (
              <div
                className="absolute z-30 mt-1 left-0 right-0 bg-card border border-border rounded-none shadow-lg max-h-[340px] overflow-y-auto"
                role="dialog"
                data-testid="room-city-popover"
              >
                {/* Your current city — highlighted, selected by default */}
                {city && (
                  <div className="px-3 py-2 border-b border-border">
                    <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Your city</div>
                    <button
                      type="button"
                      onClick={() => { setFilterCity(city); setShowCityPopover(false); }}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded-none border ${filterCity === city ? "border-border bg-[#FFBE06]" : "border-transparent hover:bg-[#FFBE06]/15"}`}
                      data-testid="room-city-current"
                    >
                      <strong>{city}</strong>
                      <span className="text-xs text-muted-foreground ml-1">· default</span>
                    </button>
                  </div>
                )}

                {/* Nearby cities within 50km */}
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Nearby cities (50 km)</div>
                  {nearbyCities.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic px-1 py-1">No nearby cities loaded.</div>
                  ) : (
                    <ul className="space-y-0.5">
                      {nearbyCities.map((nc) => (
                        <li key={nc.label}>
                          <button
                            type="button"
                            onClick={() => { setFilterCity(nc.label); setShowCityPopover(false); }}
                            className={`w-full text-left text-sm px-2 py-1.5 rounded-none flex items-center justify-between gap-2 hover:bg-[#FFBE06]/15 ${filterCity === nc.label ? "bg-[#FFBE06] border border-border" : ""}`}
                            data-testid={`room-city-nearby-${nc.label.split(",")[0].trim().toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span className="truncate">{nc.label}</span>
                            {typeof nc.distance_km === "number" && (
                              <span className="text-xs text-muted-foreground shrink-0">{nc.distance_km.toFixed(1)} km</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Free-text search */}
                <div className="px-3 py-2 sticky bottom-0 bg-card border-t border-border">
                  <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Search any city</div>
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    placeholder="Type a city name…"
                    className="w-full border border-border rounded-none px-2 py-1.5 text-sm outline-none focus:border-foreground/40"
                    data-testid="room-city-search-input"
                  />
                  {citySearchResults.length > 0 && (
                    <ul className="mt-1 max-h-32 overflow-y-auto" data-testid="room-city-search-results">
                      {citySearchResults.map((c) => (
                        <li key={c}>
                          <button
                            type="button"
                            onClick={() => { setFilterCity(c); setShowCityPopover(false); }}
                            className={`w-full text-left text-sm px-2 py-1.5 rounded-none hover:bg-[#FFBE06]/15 ${filterCity === c ? "bg-[#FFBE06] border border-border" : ""}`}
                          >
                            {c}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-3">
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1">
              <Calendar size={11} /> Intended month
            </label>
            <button
              type="button"
              onClick={() => setShowDateFilter(true)}
              className="mt-1 w-full border border-border rounded-none px-2 py-2 text-sm bg-card text-left hover:border-foreground/30 flex items-center justify-between gap-2"
              data-testid="room-filter-date"
            >
              <span className="truncate">{filterDateLabel}</span>
              <Calendar size={14} className="text-muted-foreground shrink-0" />
            </button>
          </div>

          {(filterCity !== "all" || filterDate !== "all") && (
            <button
              type="button"
              onClick={() => { setFilterCity("all"); setFilterDate("all"); }}
              className="mt-3 text-xs underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="room-filter-reset"
            >
              <RotateCcw size={11} /> Reset filters
            </button>
          )}

          <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">
            Showing <strong className="text-foreground">{messages.length}</strong> message{messages.length === 1 ? "" : "s"} in the current view.
          </div>
        </aside>

          <RoomDealsBox
            productId={product.id}
            city={filterCity !== "all" ? filterCity : city}
            date={
              filterDate !== "all"
                ? filterDate
                : (intentDate && intentDate !== "admin" && intentDate !== "pending" ? intentDate : null)
            }
          />
        </div>
      </div>


      {showIntent && (
        <IntentDateModal
          productId={productId}
          city={city}
          productName={product.product || product.name}
          initialMyDate={(intentDate !== "admin" && intentDate !== "pending") ? intentDate : null}
          onClose={() => setShowIntent(false)}
          onSaved={(d) => { setIntentDate(d); setShowIntent(false); }}
        />
      )}

      {showDateFilter && (
        <IntentDateModal
          productId={productId}
          city={city}
          productName={product.product || product.name}
          initialMyDate={filterDate !== "all" ? filterDate : null}
          pickerMode
          title="Filter chat by intended date"
          onClose={() => setShowDateFilter(false)}
          onPick={(d) => { setFilterDate(d); setShowDateFilter(false); }}
        />
      )}

      {showHire && (
        <HireNegotiatorModal
          product={product}
          onClose={() => setShowHire(false)}
          onDone={(req) => { setShowHire(false); setHireStatus({ has_request: true, by_me: true, intended_buyers: req.intended_buyers, created_at: req.created_at }); }}
        />
      )}

      {showExit && (
        <ExitRoomModal
          product={product}
          city={city}
          onClose={() => setShowExit(false)}
          onExited={() => {
            unmarkJoined?.(productId);
            try { window.dispatchEvent(new CustomEvent("bhega:changed", { detail: { productId } })); } catch (_) { /* noop */ }
            toast.success("You've exited the negotiation room.");
            navigate(product?.category ? `/${product.category}` : "/browse", { replace: true });
          }}
        />
      )}

      {showAdminBuyers && (
        <AdminBuyersModal product={product} onClose={() => setShowAdminBuyers(false)} />
      )}
    </div>
  );
}

function ExitRoomModal({ product, city, onClose, onExited }) {
  useEscape(true, onClose);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error("Please pick a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/exit-room`, { reason, city: city || "" });
      onExited?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not exit"));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-md rounded-none border border-border" onClick={(e) => e.stopPropagation()} data-testid="exit-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-[var(--bhega-danger-soft)]">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <LogOut size={18} className="text-[var(--bhega-danger)]" /> Exit the negotiation room
            </h3>
            <div className="text-xs text-muted-foreground">Tell us why you&apos;re leaving. This helps us improve BHEGA.</div>
          </div>
          <button onClick={onClose} data-testid="exit-modal-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-5 space-y-3 text-sm">
          {EXIT_REASONS.map((r) => (
            <label key={r.key} className={`flex items-center gap-3 p-3 border rounded-none cursor-pointer transition-colors ${reason === r.key ? "border-border bg-[#FFBE06]/10" : "border-border hover:border-foreground/30"}`} data-testid={`exit-reason-${r.key}`}>
              <input type="radio" name="exit-reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} className="accent-[#FFBE06]" />
              <span className="font-medium">{r.label}</span>
            </label>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="exit-modal-cancel">Cancel</button>
          <button disabled={!reason || busy} onClick={submit} className="inline-flex items-center gap-2 px-4 min-h-[44px] bg-[var(--bhega-danger)] text-white font-bold text-sm hover:opacity-90 disabled:opacity-50" data-testid="exit-modal-submit">
            {busy ? "Exiting…" : "Confirm exit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminBuyersModal({ product, onClose }) {
  useEscape(true, onClose);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: "", date: "", state: "" });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.city) params.city = f.city;
      if (f.date) params.date = f.date;
      if (f.state) params.scope = f.state === "All India" ? "global" : `state:${f.state}`;
      const r = await api.get(`/admin/products/${product.id}/intended-buyers`, { params });
      setBuyers(r.data.buyers || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load buyers"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, []);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.city) params.set("city", filters.city);
      if (filters.date) params.set("date", filters.date);
      if (filters.state) params.set("scope", filters.state === "All India" ? "global" : `state:${filters.state}`);
      const token = localStorage.getItem("bhega_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/admin/products/${product.id}/intended-buyers.csv?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `intended-buyers-${product.product || product.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const distinctCities = Array.from(new Set(buyers.map((b) => b.city).filter(Boolean)));
  const distinctDates = Array.from(new Set(buyers.map((b) => b.date).filter(Boolean))).sort();
  const distinctStates = Array.from(new Set(buyers.map((b) => b.state).filter(Boolean)));

  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-4xl rounded-none border border-border max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="admin-buyers-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-[#FFBE06]/10">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <Users size={18} /> Intended buyers — {product.product || product.name}
            </h3>
            <div className="text-xs text-muted-foreground">Admin-only · name, phone, intended date, city &amp; state of every buyer who picked a date.</div>
          </div>
          <button onClick={onClose} data-testid="admin-buyers-close"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-border grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">State</label>
            <select value={filters.state} onChange={(e) => { const f = { ...filters, state: e.target.value }; setFilters(f); load(f); }} className="mt-1 w-full border border-border rounded-none px-2 py-1.5" data-testid="admin-filter-state">
              <option value="">All</option>
              {distinctStates.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">City</label>
            <select value={filters.city} onChange={(e) => { const f = { ...filters, city: e.target.value }; setFilters(f); load(f); }} className="mt-1 w-full border border-border rounded-none px-2 py-1.5" data-testid="admin-filter-city">
              <option value="">All</option>
              {distinctCities.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Intended month</label>
            <select value={filters.date} onChange={(e) => { const f = { ...filters, date: e.target.value }; setFilters(f); load(f); }} className="mt-1 w-full border border-border rounded-none px-2 py-1.5" data-testid="admin-filter-date">
              <option value="">All</option>
              {distinctDates.map((d) => (<option key={d} value={d}>{shortDate(d)}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={exportCSV} className="btn-primary text-sm w-full" data-testid="admin-buyers-export">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Loading…</div>
          ) : buyers.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="admin-buyers-empty">No buyers match these filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Name</th>
                  <th className="text-left px-4 py-2 font-bold">Phone</th>
                  <th className="text-left px-4 py-2 font-bold">Intended month</th>
                  <th className="text-left px-4 py-2 font-bold">City</th>
                  <th className="text-left px-4 py-2 font-bold">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-testid="admin-buyers-table">
                {buyers.map((b, i) => (
                  <tr key={`${b.user_id}-${i}`} className="hover:bg-foreground/5">
                    <td className="px-4 py-2 font-semibold">{b.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{b.phone_number}</td>
                    <td className="px-4 py-2">{b.month_label || (b.date ? shortDate(b.date) : "—")}</td>
                    <td className="px-4 py-2">{b.city || "—"}</td>
                    <td className="px-4 py-2">{b.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>Total: <strong className="text-foreground" data-testid="admin-buyers-total">{buyers.length}</strong></span>
          <button onClick={onClose} className="btn-outline text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

function HireNegotiatorModal({ product, onClose, onDone }) {
  useEscape(true, onClose);
  const { city } = useAuth();
  const [buyers, setBuyers] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const n = parseInt(buyers, 10);
  const canSubmit = !isNaN(n) && n >= 10;
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/hire-negotiator", { product_id: product.id, intended_buyers: n, city: city || "", notes });
      toast.success("Hire Negotiator request submitted!");
      onDone(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to submit request"));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-lg rounded-none border border-border" onClick={(e) => e.stopPropagation()} data-testid="hire-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-[#FFBE06]/10">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Briefcase size={18} /> Hire a Negotiator</h3>
            <div className="text-xs text-muted-foreground">We&apos;ll connect you with a professional negotiator for this group buy.</div>
          </div>
          <button onClick={onClose} data-testid="hire-modal-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-5 space-y-4 text-sm">
          <div className="border border-border rounded-none p-3 bg-foreground/5">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Product (auto-filled)</div>
            <div className="font-heading font-bold mt-1">{product.product || product.name}</div>
            <div className="text-xs text-muted-foreground">{[product.brand, product.typology || product.variant, product.category_label].filter(Boolean).join(" · ")}</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Intended buyers *</label>
            <input type="number" min="1" value={buyers} onChange={(e) => setBuyers(e.target.value)} placeholder="Enter at least 10" className="input-bhega mt-1" data-testid="hire-buyers-input" />
            <div className={`text-xs mt-1 ${canSubmit ? "text-[var(--bhega-success)]" : "text-muted-foreground"}`}>
              {!buyers ? "Submit unlocks at 10+ buyers." : canSubmit ? `✓ Submit unlocked — ${n} intended buyers` : `Need ${10 - n} more buyer${10 - n === 1 ? "" : "s"} to unlock submit`}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context — finance preferences, timeline, expected discount…" className="input-bhega mt-1" data-testid="hire-notes-input" />
          </div>
          <div className="text-xs text-muted-foreground flex items-start gap-2">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>Only <strong>one Hire Negotiator request per product</strong> is allowed. Once submitted, no further requests can be raised for this product.</span>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button disabled={!canSubmit || busy} onClick={submit} className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="hire-submit">
            {busy ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}


function RoomsRail({ activeId }) {
  const { rooms = [], loaded } = useActivity() || {};
  return (
    <aside className="hidden lg:block self-start sticky top-[100px] bhega-card" aria-label="Your rooms" data-testid="room-rooms-rail">
      <div className="px-3 py-3 border-b border-border flex items-center justify-between">
        <span className="font-extrabold text-sm">Your rooms</span>
        <span className="text-xs text-muted-foreground num">{rooms.length}</span>
      </div>
      <div className="max-h-[calc(100vh-220px)] overflow-y-auto divide-y divide-border">
        {!loaded ? (
          <div className="p-3 space-y-2"><div className="skeleton h-10" /><div className="skeleton h-10" /></div>
        ) : rooms.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground">Rooms you join will appear here so you can jump between conversations.</p>
        ) : rooms.map((r) => {
          const active = r.product_id === activeId;
          return (
            <Link
              key={`${r.product_id}-${r.scope}`}
              to={`/${r.product_slug || r.product_id}`}
              className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${active ? "bg-[var(--bhega-primary-soft)] shadow-[inset_3px_0_0_0_#FFBE06]" : "hover:bg-raised"}`}
              aria-current={active ? "page" : undefined}
              data-testid={`rail-room-${r.product_id}`}
            >
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-bold truncate">{r.product_name}</span>
                <span className={`block text-xs truncate num text-muted-foreground`}>{r.bhega_count} buyers · {shortMonthCompact(r.date)}</span>
              </span>
              {!active && r.unread_count > 0 && <span className="badge-count">{r.unread_count}</span>}
            </Link>
          );
        })}
      </div>
      <Link to="/browse" className="flex items-center justify-center gap-1 px-3 py-2.5 border-t border-border text-xs font-bold hover:bg-raised" data-testid="rail-explore">
        Find more rooms <ArrowRight size={12} />
      </Link>
    </aside>
  );
}
